/**
 * +prof: herb and ore nodes on open maps (docs/design/professions.md §5). One instanced toon mesh per
 * node kind (outlined like the other props) built from primitives with baked vertex colours:
 *   Sunleaf      a fan of golden-green blades with yellow buds
 *   Marshbell    thin stems hung with pale-blue bells
 *   Bloomcap     a cluster of glowing orange mushroom caps
 *   Starpetal    a silver five-petal star flower with a violet heart
 *   Rustvein     a squat grey-brown outcrop with rust streaks
 *   Tallow Seam  a boulder with a waxy yellow band
 *   Ashen Vein   a dark slate rock bristling with pale flakes
 *   Coilstone    a big green-black lode wrapped in a glowing coil
 * A soft twinkle card floats over every ready node so it reads at a distance (brighter and higher
 * over nodes of the tracked profession). A depleted node shrinks to a dim, grey stub until it is
 * back; the node being gathered shakes a little.
 */
import * as THREE from 'three';
import type { DeepReadonly, GatherNodeDef, GatherProfession, GatherState } from '@keystone/sim';
import { gatherNodeKind, type GatherNodeKind } from '@keystone/content';
import { U, addHull, ownedToonMaterial, type HullRegistry } from '../style';
import { blob, combine, paint, rockGeo, xf } from './procs';


type V3 = readonly [number, number, number];

/** Seeded RNG for mesh lumps (per kind, fixed). */
function rnd(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const hex = (s: string): number => parseInt(s.slice(1), 16);
const cyl = (rt: number, rb: number, h: number, seg = 7): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg).translate(0, h / 2, 0);
const cone = (r: number, h: number, seg = 7): THREE.BufferGeometry => new THREE.ConeGeometry(r, h, seg).translate(0, h / 2, 0);
const sph = (r: number, ws = 8, hs = 6): THREE.BufferGeometry => new THREE.SphereGeometry(r, ws, hs);

/** Build the mesh of a node kind (yards, origin at the base, facing +z). */
export function nodeGeometry(kind: GatherNodeKind): THREE.BufferGeometry {
  const R = rnd(kind.id.length * 7919 + kind.tier * 131);
  const c = hex(kind.look.color);
  const a = hex(kind.look.accent);
  const parts: THREE.BufferGeometry[] = [];
  switch (kind.look.mesh) {
    case 'leafy': {
      for (let i = 0; i < 7; i++) {
        const ang = (i / 7) * Math.PI * 2 + R() * 0.4;
        const tilt = 0.55 + R() * 0.35;
        parts.push(paint(xf(cone(0.09, 0.62 + R() * 0.2, 5), [Math.cos(ang) * 0.06, 0, Math.sin(ang) * 0.06], [Math.sin(ang) * tilt, 0, -Math.cos(ang) * tilt], [1, 1, 0.35]), c, [0.6, 1.15]));
      }
      for (let i = 0; i < 3; i++) parts.push(paint(xf(sph(0.07), [(R() - 0.5) * 0.25, 0.5 + R() * 0.15, (R() - 0.5) * 0.25]), a, [1, 1.1]));
      break;
    }
    case 'bell': {
      parts.push(paint(xf(blob(0.22, R, 0.3, 7, 5, 0.6), [0, 0.06, 0], [0, 0, 0], [1.3, 0.45, 1.3]), c, [0.6, 1.0]));
      for (let i = 0; i < 4; i++) {
        const ang = (i / 4) * Math.PI * 2 + R();
        const x = Math.cos(ang) * 0.12;
        const z = Math.sin(ang) * 0.12;
        const h = 0.55 + R() * 0.3;
        parts.push(paint(xf(cyl(0.015, 0.02, h, 4), [x, 0, z], [Math.cos(ang) * 0.15, 0, Math.sin(ang) * 0.15]), c, [0.7, 1]));
        parts.push(paint(xf(cone(0.08, 0.13, 7), [x + Math.cos(ang) * 0.08, h - 0.05, z + Math.sin(ang) * 0.08], [Math.PI, 0, 0]), a, [0.9, 1.15]));
      }
      break;
    }
    case 'mushroom': {
      const caps: [number, number, number, number][] = [
        [0, 0, 0.3, 0.48],
        [0.22, 0.1, 0.2, 0.32],
        [-0.18, 0.14, 0.17, 0.26],
        [0.05, -0.22, 0.14, 0.22],
      ];
      for (const [x, z, r, h] of caps) {
        parts.push(paint(xf(cyl(r * 0.28, r * 0.36, h, 6), [x, 0, z]), 0xf1e2c4, [0.75, 1.05]));
        parts.push(paint(xf(sph(r, 9, 5), [x, h, z], [0, 0, 0], [1, 0.55, 1]), c, [0.85, 1.15]));
        parts.push(paint(xf(sph(r * 0.22, 5, 4), [x + r * 0.4, h + r * 0.38, z]), a, [1, 1]));
      }
      break;
    }
    case 'star': {
      parts.push(paint(xf(blob(0.2, R, 0.25, 7, 5, 0.6), [0, 0.04, 0], [0, 0, 0], [1.4, 0.4, 1.4]), 0x6d8a6a, [0.6, 1]));
      parts.push(paint(cyl(0.02, 0.025, 0.62, 5), 0x7f9a80, [0.7, 1]));
      for (let i = 0; i < 5; i++) {
        const ang = (i / 5) * Math.PI * 2;
        parts.push(paint(xf(cone(0.09, 0.3, 4), [Math.cos(ang) * 0.02, 0.62, Math.sin(ang) * 0.02], [Math.sin(ang) * 1.25, 0, -Math.cos(ang) * 1.25], [1, 1, 0.3]), c, [0.95, 1.15]));
      }
      parts.push(paint(xf(sph(0.07), [0, 0.66, 0]), a, [1, 1.2]));
      break;
    }
    case 'outcrop': {
      parts.push(paint(xf(rockGeo(0.55, R, 0.6), [0, 0.2, 0]), c, [0.65, 1.05]));
      parts.push(paint(xf(rockGeo(0.32, R, 0.7), [0.45, 0.1, 0.15]), c, [0.65, 1.05]));
      for (let i = 0; i < 4; i++) parts.push(paint(xf(sph(0.09, 5, 4), [(R() - 0.5) * 0.6, 0.3 + R() * 0.25, 0.35 + R() * 0.1], [0, 0, 0], [1.6, 0.6, 0.6]), a, [0.9, 1.1]));
      break;
    }
    case 'seam': {
      parts.push(paint(xf(rockGeo(0.7, R, 0.75), [0, 0.3, 0]), c, [0.6, 1.05]));
      parts.push(paint(xf(new THREE.TorusGeometry(0.62, 0.08, 4, 14), [0, 0.45, 0], [Math.PI / 2 + 0.25, 0, 0.1], [1, 1, 0.8]), a, [0.95, 1.1]));
      parts.push(paint(xf(rockGeo(0.3, R, 0.7), [-0.6, 0.1, 0.25]), c, [0.65, 1.05]));
      break;
    }
    case 'vein': {
      parts.push(paint(xf(rockGeo(0.75, R, 0.85), [0, 0.38, 0]), c, [0.55, 1.0]));
      for (let i = 0; i < 6; i++) {
        const ang = R() * Math.PI * 2;
        parts.push(paint(xf(new THREE.BoxGeometry(0.08, 0.32, 0.18), [Math.cos(ang) * 0.55, 0.45 + R() * 0.35, Math.sin(ang) * 0.55], [R() - 0.5, ang, (R() - 0.5) * 0.8]), a, [1, 1.15]));
      }
      break;
    }
    case 'lode': {
      parts.push(paint(xf(rockGeo(0.95, R, 0.95), [0, 0.5, 0]), c, [0.55, 1.0]));
      parts.push(paint(xf(rockGeo(0.45, R, 0.8), [0.8, 0.15, -0.2]), c, [0.6, 1.0]));
      parts.push(paint(xf(new THREE.TorusGeometry(0.85, 0.09, 5, 18, Math.PI * 1.6), [0, 0.65, 0], [Math.PI / 2 - 0.3, 0, 0.4]), a, [1.1, 1.3]));
      parts.push(paint(xf(new THREE.TorusGeometry(0.7, 0.07, 5, 16, Math.PI * 1.3), [0, 0.95, 0], [Math.PI / 2 + 0.35, 0, -0.5]), a, [1.1, 1.3]));
      break;
    }
  }
  const g = combine(parts);
  g.computeBoundingSphere();
  return g;
}

/** Drawn scale per mesh family (herbs read a bit bigger than life so they show in the grass). */
const SCALE: Record<GatherNodeKind['look']['mesh'], number> = { leafy: 1.6, bell: 1.5, mushroom: 1.4, star: 1.5, outcrop: 1.1, seam: 1.05, vein: 1.05, lode: 1.05 };

/** Sparkle card height over a node (yards) by mesh family. */
const SPARKLE_Y: Record<GatherNodeKind['look']['mesh'], number> = { leafy: 1.3, bell: 1.4, mushroom: 1.25, star: 1.5, outcrop: 1.1, seam: 1.35, vein: 1.5, lode: 2.0 };

const hexRgb = (s: string): V3 => {
  const c = new THREE.Color(s);
  return [c.r, c.g, c.b];
};

interface Slot {
  def: GatherNodeDef;
  kind: GatherNodeKind;
  mesh: THREE.InstancedMesh;
  index: number;
  sparkle: number;
  x: number;
  y: number;
  z: number;
  /** Last applied state: 0 ready, 1 depleted, 2 being gathered. */
  shown: number;
}

const M = new THREE.Matrix4();
const Q = new THREE.Quaternion();
const E = new THREE.Euler();
const S = new THREE.Vector3();
const T = new THREE.Vector3();
const WHITE = new THREE.Color(1, 1, 1);
const DIM = new THREE.Color(0.38, 0.38, 0.4);

export class GatherNodesView {
  readonly group = new THREE.Group();
  private readonly slots: Slot[] = [];
  private readonly byId = new Map<string, Slot>();
  private readonly meshes: THREE.InstancedMesh[] = [];
  private readonly sparkles: THREE.InstancedMesh;
  private readonly owned: { dispose(): void }[] = [];
  private tracked: GatherProfession | null = null;
  private trackedDirty = true;

  constructor(nodes: readonly DeepReadonly<GatherNodeDef>[], groundY: (x: number, y: number) => number, hulls: HullRegistry | null) {
    this.group.name = 'gather-nodes';
    const byKind = new Map<string, DeepReadonly<GatherNodeDef>[]>();
    for (const n of nodes) {
      if (!gatherNodeKind(n.kind)) continue;
      const l = byKind.get(n.kind) ?? [];
      l.push(n);
      byKind.set(n.kind, l);
    }
    const mat = ownedToonMaterial({ color: 0xffffff, vertexColors: true });
    mat.name = 'gather-nodes';
    this.owned.push(mat);
    for (const [kindId, list] of byKind) {
      const kind = gatherNodeKind(kindId)!;
      const geo = nodeGeometry(kind);
      this.owned.push(geo);
      const mesh = new THREE.InstancedMesh(geo, mat, list.length);
      mesh.name = `gather-${kindId}`;
      mesh.frustumCulled = false;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
      list.forEach((n, i) => {
        const slot: Slot = { def: n as GatherNodeDef, kind, mesh, index: i, sparkle: this.slots.length, x: n.pos.x, y: groundY(n.pos.x, n.pos.y), z: -n.pos.y, shown: -1 };
        this.slots.push(slot);
        this.byId.set(n.id, slot);
        mesh.setColorAt(i, WHITE);
      });
      addHull(mesh, mat, hulls, 'prop');
      this.meshes.push(mesh);
    }
    this.sparkles = this.sparkleCards();
    this.group.add(this.sparkles);
    for (const s of this.slots) this.place(s, 0, 0);
  }

  /** Highlight nodes of one profession (the tracking toggle), or none. */
  setTracked(prof: GatherProfession | null): void {
    if (prof === this.tracked) return;
    this.tracked = prof;
    this.trackedDirty = true;
  }

  /** Per frame: depletion, the node being gathered, sparkles. `state` is the sim's (read-only). */
  update(state: DeepReadonly<GatherState> | null | undefined, timeMs: number): void {
    const busy = new Set<string>();
    for (const ch of Object.values(state?.channels ?? {})) busy.add(ch.nodeId);
    const dirty = new Set<THREE.InstancedMesh>();
    let sparkDirty = this.trackedDirty;
    for (const s of this.slots) {
      const n = state?.nodes[s.def.id];
      const depleted = !!n && timeMs < n.readyAt;
      const st = depleted ? 1 : busy.has(s.def.id) ? 2 : 0;
      if (st === 2) {
        // Shake while it is being gathered.
        this.place(s, st, timeMs);
        dirty.add(s.mesh);
      } else if (st !== s.shown || this.trackedDirty) {
        this.place(s, st, timeMs);
        dirty.add(s.mesh);
        sparkDirty = true;
      }
      if (st !== s.shown) sparkDirty = true;
      s.shown = st;
    }
    for (const m of dirty) {
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
    if (sparkDirty) {
      for (const s of this.slots) this.placeSparkle(s);
      this.sparkles.instanceMatrix.needsUpdate = true;
    }
    this.trackedDirty = false;
  }

  private place(s: Slot, st: number, timeMs: number): void {
    const k = (st === 1 ? 0.55 : 1) * SCALE[s.kind.look.mesh];
    const wob = st === 2 ? Math.sin(timeMs * 0.03) * 0.06 : 0;
    M.compose(T.set(s.x, s.y - (st === 1 ? 0.05 : 0), s.z), Q.setFromEuler(E.set(wob, s.def.facing ?? 0, wob * 0.6)), S.set(k, st === 1 ? k * 0.6 : k * (1 + Math.abs(wob)), k));
    s.mesh.setMatrixAt(s.index, M);
    s.mesh.setColorAt(s.index, st === 1 ? DIM : WHITE);
  }

  private placeSparkle(s: Slot): void {
    const ready = s.shown !== 1;
    const tracked = this.tracked === s.def.profession;
    const size = !ready ? 0 : tracked ? 1.8 : 0.95 + s.def.tier * 0.1;
    const lift = SPARKLE_Y[s.kind.look.mesh] + (tracked ? 0.6 : 0);
    M.makeScale(size, size, size).setPosition(s.x, s.y + lift, s.z);
    this.sparkles.setMatrixAt(s.sparkle, M);
  }

  /** Additive twinkle cards (one instanced draw): a soft halo with a four-point glint that blinks. */
  private sparkleCards(): THREE.InstancedMesh {
    const geo = new THREE.PlaneGeometry(1, 1);
    const color = new Float32Array(this.slots.length * 4);
    this.slots.forEach((s, i) => {
      const c = hexRgb(s.kind.look.accent);
      color.set([c[0], c[1], c[2], (i * 0.618) % 1], i * 4);
    });
    geo.setAttribute('aColor', new THREE.InstancedBufferAttribute(color, 4));
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTime: U.uTime, uFx: U.uFx },
      vertexShader: `attribute vec4 aColor; varying vec2 vUv; varying vec4 vColor;
        void main(){
          vUv = uv; vColor = aColor;
          vec4 c = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          float s = length(instanceMatrix[0].xyz);
          vec4 mv = modelViewMatrix * c;
          mv.y += 0.08 * sin(c.x * 0.7 + c.z * 0.3 + aColor.a * 6.28);
          mv.xy += position.xy * s;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `uniform float uTime; uniform float uFx; varying vec2 vUv; varying vec4 vColor;
        void main(){
          vec2 p = vUv * 2.0 - 1.0;
          float r = length(p); if (r > 1.0) discard;
          float halo = pow(max(0.0, 1.0 - r), 2.6) * 0.7;
          float glint = max(0.0, 1.0 - abs(p.x) * 9.0 - abs(p.y) * 1.3) + max(0.0, 1.0 - abs(p.y) * 9.0 - abs(p.x) * 1.3);
          float blink = 0.35 + 0.65 * pow(0.5 + 0.5 * sin(uTime * 2.6 + vColor.a * 40.0), 3.0);
          float a = (halo + glint * 0.9 * blink) * min(1.2, uFx);
          gl_FragColor = vec4(mix(vColor.rgb, vec3(1.0), 0.35) * a, a);
        }`,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.owned.push(geo, mat);
    const m = new THREE.InstancedMesh(geo, mat, Math.max(1, this.slots.length));
    m.frustumCulled = false;
    m.renderOrder = 9;
    m.name = 'gather-sparkles';
    return m;
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const m of this.meshes) m.dispose();
    this.sparkles.dispose();
    for (const o of this.owned) o.dispose();
  }
}
